import React, { useEffect } from "react";
import { Switch, Route, Redirect } from "wouter";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { MissedCallToasts } from "@/components/MissedCallToasts";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/contexts/auth-context";
import { SequenceConfigProvider } from "@/contexts/sequence-config-context";
import { ExpansionProvider } from "@/contexts/expansion-context";
import { AdminPulseVisibilityProvider } from "@/contexts/admin-pulse-visibility-context";
import { DialogProvider } from "@/contexts/dialog-context";
import { WebSocketProvider } from "@/contexts/websocket-context";
import { TableRenderingProvider, useBatchedUpdate } from "@/contexts/table-rendering-context";
import ProjectDashboard from "@/pages/project-dashboard";
import LoginPage from "@/pages/login";
import NotFound from "@/pages/not-found";
import AdminTranscriptsListPage from "@/pages/admin/transcripts-list";
import AdminTranscriptsDetailPage from "@/pages/admin/transcripts-detail";
import { useAuth } from "@/contexts/auth-context";
import { useScrollMonitoring } from "@/hooks/use-scroll-monitoring";
import { useAudioNotifierInit } from "@/hooks/use-audio-notifier-init";
import { AlertTriangle } from "lucide-react";
import { ChatDock } from "@/components/chat-dock/ChatDock";
import { PreviewShareDialog } from "@/components/preview-share-dialog";
import { StatusOverlay } from "@/components/status-overlay";
import { CallProvider } from "@/contexts/call-context";
import { IncomingCallModal } from "@/components/calls/IncomingCallModal";
import { ClientIncomingCallModal } from "@/components/calls/ClientIncomingCallModal";
import { RingbackPlayer } from "@/components/calls/RingbackPlayer";
import { InCallWindow } from "@/components/calls/InCallWindow";
import { NowProvider } from "@/components/timeline/useNow";
import { PreCallLobby } from "@/components/calls/PreCallLobby";
import { ShortcutHelpOverlay } from "@/components/shortcut-help-overlay";
import { QuickSearchDialog } from "@/components/quick-search/QuickSearchDialog";
import { registerShortcut } from "@/lib/shortcut-registry";
import { LegalFooter } from "@/components/legal-footer";
import { CalendarDeepLinkHandler } from "@/components/calendar/CalendarDeepLinkHandler";
import { ShareEmailDeliveryMonitor } from "@/components/share-email-delivery-monitor";
import { TimeTrackHost } from "@/components/timetrack";

// Lazy-load DebugPanel only in development mode
const DebugPanel = import.meta.env.DEV 
  ? React.lazy(() => import("@/components/debug-panel").then(m => ({ default: m.DebugPanel })))
  : null;

/**
 * Task #245 — Spotlight quick-search palette mount. Owns the global
 * Cmd/Ctrl+K (and `/` fallback) keydown listener, registers both
 * shortcuts in the `?` help sheet, and renders `<QuickSearchDialog>`
 * once at the app root so it floats above every page.
 *
 * The listener uses `keydown` with `metaKey||ctrlKey` so the modifier
 * bypasses `useGlobalHotkey`'s single-key bail matrix naturally — we
 * deliberately do NOT route this through `useGlobalHotkey`, which has
 * no modifier-combo support.
 */
function QuickSearchMount() {
  const { isAuthenticated, isLoading } = useAuth();
  const [open, setOpen] = React.useState(false);

  React.useEffect(() => {
    // Register both shortcuts in the `?` help sheet (task #245 acceptance #12).
    const unregisterCmdK = registerShortcut({
      id: 'quick-search-cmd-k',
      key: '⌘K / Ctrl+K',
      description: 'Open quick search',
      group: 'Navigation',
    });
    const unregisterSlash = registerShortcut({
      id: 'quick-search-slash',
      key: '/',
      description: 'Open quick search (when no field is focused)',
      group: 'Navigation',
    });
    return () => {
      unregisterCmdK();
      unregisterSlash();
    };
  }, []);

  React.useEffect(() => {
    if (isLoading || !isAuthenticated) return;
    const handler = (e: KeyboardEvent) => {
      // Cmd/Ctrl+K — bypasses any bail matrix because of the modifier.
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setOpen(prev => !prev);
        return;
      }
      // `/` — only when no input is focused (GitHub-style fallback).
      if (e.key === '/' && !e.metaKey && !e.ctrlKey && !e.altKey) {
        const tag = (document.activeElement?.tagName ?? '').toLowerCase();
        const isEditable =
          tag === 'input' ||
          tag === 'textarea' ||
          tag === 'select' ||
          (document.activeElement as HTMLElement | null)?.isContentEditable;
        if (!isEditable) {
          e.preventDefault();
          setOpen(true);
        }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isAuthenticated, isLoading]);

  if (isLoading || !isAuthenticated) return null;
  return <QuickSearchDialog open={open} onOpenChange={setOpen} />;
}

function AuthenticatedPreviewShareHost() {
  const { isAuthenticated, isLoading, user } = useAuth();
  const [state, setState] = React.useState<{ projectId?: string; pulseId?: number; shareType?: 'preview' | 'final' } | null>(null);

  React.useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail || {};
      setState({ projectId: detail.projectId, pulseId: detail.pulseId, shareType: detail.shareType });
    };
    window.addEventListener('open-preview-share', handler);
    return () => window.removeEventListener('open-preview-share', handler);
  }, []);

  if (isLoading || !isAuthenticated || !state) return null;
  return (
    <PreviewShareDialog
      open={!!state}
      onOpenChange={(open) => { if (!open) setState(null); }}
      projectId={state.projectId}
      pulseId={state.pulseId}
      requestedShareType={state.shareType}
      senderName={
        user?.firstName?.trim()
        || user?.username
        || 'Acolori Team'
      }
    />
  );
}

function AuthenticatedChatDock() {
  const { isAuthenticated, isLoading } = useAuth();
  
  if (isLoading || !isAuthenticated) return null;
  
  return <ChatDock />;
}

function AuthenticatedStatusOverlay() {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading || !isAuthenticated) return null;

  return <StatusOverlay />;
}

function RecoveryModeBanner() {
  const { isRecoveryMode } = useAuth();
  
  if (!isRecoveryMode) return null;
  
  return (
    <div 
      className="fixed top-0 left-0 right-0 z-[9999] bg-amber-600 text-white px-4 py-2 flex items-center justify-center gap-2 shadow-lg"
      data-testid="recovery-mode-banner"
    >
      <AlertTriangle className="h-5 w-5 flex-shrink-0" />
      <span className="text-sm font-medium">
        Recovery Mode Active: Database is empty. Log in as Admin with the one-time password from the server logs, then restore a backup or create an admin user in Settings.
      </span>
    </div>
  );
}

function ProtectedRoute({ component: Component }: { component: React.ComponentType }) {
  const { isAuthenticated, isLoading } = useAuth();
  
  // Show loading spinner while authentication is being validated
  if (isLoading) {
    return (
      <div className="min-h-screen navy-bg flex items-center justify-center">
        <div className="text-gray-400">Loading...</div>
      </div>
    );
  }
  
  // Redirect to login if not authenticated after validation completes
  if (!isAuthenticated) {
    return <Redirect to="/login" />;
  }
  
  return <Component />;
}

function Router() {
  return (
    <Switch>
      <Route path="/login" component={LoginPage} />
      <Route path="/admin/transcripts">
        {() => <ProtectedRoute component={AdminTranscriptsListPage} />}
      </Route>
      <Route path="/admin/transcripts/:sessionId">
        {() => <ProtectedRoute component={AdminTranscriptsDetailPage} />}
      </Route>
      <Route path="/">
        {() => <ProtectedRoute component={ProjectDashboard} />}
      </Route>
      <Route component={NotFound} />
    </Switch>
  );
}

// Integrated providers component that connects WebSocket with TableRenderingManager
function IntegratedProviders({ children }: { children: React.ReactNode }) {
  const batchedUpdate = useBatchedUpdate();
  
  // Initialize audio notifier with user's sound preferences
  useAudioNotifierInit();
  
  const handleTableUpdate = React.useCallback((update: any) => {
    // Forward WebSocket updates to TableRenderingManager
    batchedUpdate.queueUpdate(update);
  }, [batchedUpdate]);
  
  return (
    <WebSocketProvider onTableUpdate={handleTableUpdate}>
      {children}
    </WebSocketProvider>
  );
}

function App() {
  // Initialize scroll position monitoring for audit logging
  useScrollMonitoring();

  useEffect(() => {
    const preventDefaultDrop = (e: DragEvent) => {
      e.preventDefault();
    };
    window.addEventListener('dragover', preventDefaultDrop);
    window.addEventListener('drop', preventDefaultDrop);
    return () => {
      window.removeEventListener('dragover', preventDefaultDrop);
      window.removeEventListener('drop', preventDefaultDrop);
    };
  }, []);

  // Task #276 — app-wide cache invalidation for the cmd+K palette's
  // "Recent" group. Every project-open path in the app dispatches
  // `navigate-to-project` (quick-search, project-dashboard URL focus,
  // chat-dock project jumps, pulse-notification clicks). Listening
  // once here guarantees the next cmd+K open reflects the just-opened
  // project at the top of Recent, regardless of which entry point the
  // user took — the staleTime of /api/search/recent alone (60s) would
  // otherwise lag the UX.
  useEffect(() => {
    const handler = () => {
      queryClient.invalidateQueries({ queryKey: ['/api/search/recent'] });
    };
    window.addEventListener('navigate-to-project', handler);
    return () => window.removeEventListener('navigate-to-project', handler);
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <TableRenderingProvider>
          <IntegratedProviders>
            <ExpansionProvider>
              <AdminPulseVisibilityProvider>
              <DialogProvider>
                <SequenceConfigProvider>
                  <TooltipProvider>
                    <CallProvider>
                    <div className="min-h-screen navy-bg">
                      <RecoveryModeBanner />
                      <Toaster />
                      <MissedCallToasts />
                      <CalendarDeepLinkHandler />
                      <Router />
                      <LegalFooter />
                      <AuthenticatedChatDock />
                      <AuthenticatedPreviewShareHost />
                      <AuthenticatedStatusOverlay />
                      <ShareEmailDeliveryMonitor />
                      <TimeTrackHost />
                      <IncomingCallModal />
                      <ClientIncomingCallModal />
                      <RingbackPlayer />
                      <PreCallLobby />
                      <NowProvider>
                        <InCallWindow />
                      </NowProvider>
                      <ShortcutHelpOverlay />
                      <QuickSearchMount />
                      {import.meta.env.DEV && DebugPanel && (
                        <React.Suspense fallback={null}>
                          <DebugPanel />
                        </React.Suspense>
                      )}
                    </div>
                    </CallProvider>
                  </TooltipProvider>
                </SequenceConfigProvider>
              </DialogProvider>
              </AdminPulseVisibilityProvider>
            </ExpansionProvider>
          </IntegratedProviders>
        </TableRenderingProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
